import { useEffect, useRef, useState } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';

export default function LegalPageLayout({ title, lastUpdated, sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { root: null, rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const headerOffset = 100;
    const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-24 px-6 max-w-[1280px] mx-auto">
        <div className="mb-16 border-b border-[#e2e8f0] pb-8">
          <p className="text-[#00255a] text-sm mb-2 tracking-widest uppercase font-semibold">Legal Documentation</p>
          <h1 className="text-5xl font-bold text-[#00255a] mb-4">{title}</h1>
          <div className="flex items-center gap-2 text-[#64748b]">
            <span className="material-symbols-outlined text-[18px]">update</span>
            <span>Last updated: {lastUpdated}</span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-12 lg:gap-24">
          {/* Sticky Sidebar */}
          <aside className="md:w-64 flex-shrink-0">
            <div className="md:sticky md:top-32">
              <h4 className="text-xs text-[#00255a] mb-6 uppercase tracking-wider font-semibold">Table of Contents</h4>
              <nav className="flex flex-col space-y-1">
                {sections.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`text-left py-2 transition-all ${
                      activeId === s.id
                        ? 'text-[#00255a] font-bold border-l-4 border-[#00255a] pl-3 -ml-4'
                        : 'text-[#44474f] hover:text-[#00255a] pl-0'
                    }`}
                  >
                    {i + 1}. {s.title}
                  </button>
                ))}
              </nav>
              <div className="mt-12 p-6 rounded-xl bg-[#eceef0] border border-[#c4c6d1]/30">
                <h5 className="text-sm text-[#00255a] mb-2 font-semibold">Need Clarification?</h5>
                <p className="text-[#44474f] text-[13px] leading-relaxed mb-4">
                  Our team is available to discuss any clauses regarding your international placement.
                </p>
                <a className="text-[#00255a] font-bold text-[14px] flex items-center gap-1 hover:underline" href="/contact">
                  Contact Support
                  <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                </a>
              </div>
            </div>
          </aside>

          {/* Scrollable Content */}
          <article className="flex-grow max-w-3xl">
            {sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                ref={(el) => (sectionRefs.current[s.id] = el)}
                className="mb-12"
              >
                <h2 className="text-2xl font-bold text-[#00255a] mt-4 mb-6">{s.title}</h2>
                {s.content}
              </section>
            ))}
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
}